// FILENAME: frontend/src/components/SummaryCard.jsx
import React, { useMemo } from 'react';
import { Card, CardHeader, CardContent, Box, Alert, Skeleton, Chip } from '@mui/material';
import { PieChart } from '@mui/x-charts/PieChart';
import useApi from '../hooks/useApi';
import { isSyntheticCancellation } from '../utils/dataNormalizer';
import { resolveEnrollmentLifecycleStatus } from '../utils/enrollmentEvidence';
import CustomLegend from './CustomLegend';
import AnimatedChart from './AnimatedChart';

const formatProgramTitle = (programName) => {
  if (programName === 'Digital Ad Sales') return 'DigAd Sales';
  if (programName === 'Digital Ad Service') return 'DigAd Service';
  return programName;
};

const buildLegendTestId = (programName) =>
  `enrollment-status-legend-${String(programName).replace(/\s+/g, '-')}`;

const STATUS_COLORS = {
    ENROLLED: 'rgba(75, 192, 192, 0.6)',
    OPTED_OUT: 'rgba(255, 99, 132, 0.6)',
    OPT_OUT_PENDING: 'rgba(255, 159, 182, 0.6)', // New color for Opt Out Pending
    PENDING: 'rgba(153, 102, 255, 0.6)',
    ACTION_REQUIRED: 'rgba(255, 159, 64, 0.6)',
    NOT_ENROLLED: 'rgba(201, 203, 207, 0.6)',
    COOLING: 'rgba(255, 206, 86, 0.6)',
    CANCELLED: 'rgba(255, 99, 132, 0.6)',
    LIFECYCLE_UNAVAILABLE: 'rgba(201, 203, 207, 0.6)',
    default: 'rgba(153, 102, 255, 0.4)'
};

export const buildEnrollmentStatusChartData = (data) => {
    if (!Array.isArray(data)) return [];
    const activeRows = data.filter((dealer) => !isSyntheticCancellation(dealer));
    const counts = activeRows.reduce((acc, dealer) => {
        const status = resolveEnrollmentLifecycleStatus(dealer) || 'LIFECYCLE_UNAVAILABLE';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
    }, {});

    return Object.entries(counts).map(([status, count], index) => ({
        id: index,
        value: count,
        label: status,
        color: STATUS_COLORS[status] || STATUS_COLORS.default
    }));
};

const SummaryCard = ({ programName, onChartClick }) => {
    const { useEnrollmentData } = useApi();
    const { data, isLoading, error } = useEnrollmentData(programName);
    const displayTitle = formatProgramTitle(programName);
    const isDigAd = displayTitle.startsWith('DigAd');

    const chartData = useMemo(() => {
        return buildEnrollmentStatusChartData(data);
    }, [data]);

    const totalDealers = useMemo(() => chartData.reduce((sum, item) => sum + item.value, 0), [chartData]);
    const handleChartClick = (event, item) => {
        if (onChartClick && item && chartData[item.dataIndex]) {
            onChartClick({ programName, status: chartData[item.dataIndex].label });
        }
    };
    
    const handleLegendClick = (label) => {
        if (onChartClick) {
            onChartClick({ programName, status: label });
        }
    };

    return (
        <Card sx={{ height: '100%' }}>
            <CardHeader
              title={displayTitle}
              subheader="Enrollment status"
              titleTypographyProps={{
                noWrap: true,
                sx: {
                  fontSize: isDigAd ? '1rem' : '1.05rem',
                },
              }}
              action={(
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                  <Chip
                    size="small"
                    variant="outlined"
                    color="primary"
                    label={`${totalDealers} dealers`}
                  />
                </Box>
              )}
            />
            <CardContent>
                {isLoading && (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
                            <Skeleton variant="circular" width={140} height={140} />
                        </Box>
                        <Box sx={{ mt: 1 }}>
                            <Skeleton variant="text" width="70%" />
                            <Skeleton variant="text" width="55%" />
                            <Skeleton variant="text" width="60%" />
                        </Box>
                    </>
                )}
                {error && <Alert severity="error" sx={{ fontSize: '0.75rem' }}>{error.message}</Alert>}
                {data && !isLoading && (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 150 }}>
                            <AnimatedChart animationKey={`${programName}:${chartData.length}:${totalDealers}`}>
                                <PieChart
                                    series={[{ data: chartData, innerRadius: 30 }]}
                                    width={150}
                                    height={150}
                                    legend={{ hidden: true }}
                                    margin={{ top: 0, bottom: 0, left: 0, right: 0 }}
                                    onClick={handleChartClick}
                                />
                            </AnimatedChart>
                        </Box>
                        <Box
                          data-testid={buildLegendTestId(programName)}
                          sx={{ maxWidth: 220, mx: 'auto', width: '100%' }}
                        >
                          <CustomLegend data={chartData} onLegendClick={handleLegendClick} />
                        </Box>
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default SummaryCard;
